<template>
  <div class="outlook-manage">
    <div v-if="loading" class="outlook-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <p class="outlook-manage__intro">
        Kies welke {{ providerLabel }}-agenda gebruikt wordt voor nieuwe afspraken.
      </p>

      <SelectInput
        id="outlook-calendar-select"
        :label="`${providerLabel} agenda`"
        :options="calendarOptions"
        v-model="selectedCalendarId"
        placeholder="Selecteer een agenda"
      />

      <div class="outlook-manage__actions">
        <Btn variant="tertiary" :loading="refreshing" @click="loadCalendars">Ververs agenda's</Btn>
        <Btn
          variant="secondary"
          :loading="saving"
          :disabled="!selectedCalendarId"
          @click="saveCalendar"
        >
          Opslaan
        </Btn>
        <Btn variant="danger" :loading="disconnecting" @click="disconnect">
          Verbinding verbreken
        </Btn>
      </div>

      <div v-if="provider === 'outlook'" class="outlook-manage__consent">
        <p class="outlook-manage__hint">
          Externe Microsoft 365-organisatie: de beheerder opent deze link één keer.
        </p>
        <Btn
          variant="outlined"
          size="sm"
          type="button"
          :loading="copyingAdminConsent"
          @click="copyAdminConsentLink"
        >
          Kopieer admin consent link
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import IntegrationService from '@/services/integrationService';
  import GoogleCalendarService from '@/services/calendarService';

  export default {
    name: 'OutlookManageModal',
    components: { Btn, SelectInput, LoadingSpinner },
    props: {
      provider: {
        type: String,
        default: 'outlook',
        validator: (value) => ['outlook', 'google'].includes(value),
      },
      integration: {
        type: Object,
        required: true,
      },
      modalId: {
        type: [String, Number],
        default: null,
      },
      onUpdated: {
        type: Function,
        default: null,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const integrationStore = useIntegrationStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const refreshing = ref(false);
      const saving = ref(false);
      const disconnecting = ref(false);
      const copyingAdminConsent = ref(false);
      const selectedCalendarId = ref('');
      const calendars = ref([]);

      const isGoogle = computed(() => props.provider === 'google');
      const providerLabel = computed(() => (isGoogle.value ? 'Google' : 'Outlook'));

      const calendarOptions = computed(() =>
        calendars.value.map((calendar) => ({
          value: calendar.id,
          label: calendar.summary || calendar.name || calendar.id,
        }))
      );

      const loadCalendars = async () => {
        refreshing.value = true;
        try {
          const response = isGoogle.value
            ? await GoogleCalendarService.listCalendars(props.integration)
            : await IntegrationService.listOutlookCalendars(props.integration.id);
          calendars.value = Array.isArray(response) ? response : response?.calendars || [];
        } catch (error) {
          const message =
            error?.response?.data?.error ||
            error.message ||
            `Kon ${providerLabel.value}-agenda's niet laden.`;
          toastStore.addToast({ type: 'error', message });
        } finally {
          refreshing.value = false;
        }
      };

      const saveCalendar = async () => {
        if (!selectedCalendarId.value) return;
        const selectedCalendar = calendars.value.find((calendar) => calendar.id === selectedCalendarId.value);
        if (!selectedCalendar) return;

        saving.value = true;
        try {
          const settings = {
            ...(props.integration.settings || {}),
            calendar_id: selectedCalendar.id,
            calendar_name: selectedCalendar.summary || selectedCalendar.name || selectedCalendar.id,
          };

          await integrationStore.updateIntegration(props.integration.id, {
            name: props.integration.name,
            type: props.integration.type,
            is_active: props.integration.is_active,
            settings,
          });

          if (typeof props.onUpdated === 'function') {
            await props.onUpdated();
          }

          toastStore.addToast({
            type: 'success',
            message: `${providerLabel.value}-agenda opgeslagen.`,
          });
        } catch (error) {
          const message =
            error?.response?.data?.error ||
            error.message ||
            `Kon ${providerLabel.value}-agenda niet opslaan.`;
          toastStore.addToast({ type: 'error', message });
        } finally {
          saving.value = false;
        }
      };

      const copyAdminConsentLink = async () => {
        copyingAdminConsent.value = true;
        try {
          const response = await IntegrationService.getOutlookAdminConsentUrl();
          const url = response?.url;
          if (!url) {
            throw new Error('Geen admin consent link ontvangen.');
          }
          await navigator.clipboard.writeText(url);
          toastStore.addToast({ type: 'success', message: 'Admin consent link gekopieerd.' });
        } catch (error) {
          const message =
            error?.response?.data?.error ||
            error.message ||
            'Kon de admin consent link niet kopiëren.';
          toastStore.addToast({ type: 'error', message });
        } finally {
          copyingAdminConsent.value = false;
        }
      };

      const disconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: `${providerLabel.value}-koppeling verbreken?`,
          description: `${providerLabel.value}-synchronisatie stopt direct voor nieuwe afspraken.`,
          status: 'danger',
          submitText: 'Verbreek verbinding',
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await integrationStore.deleteIntegration(props.integration.id);
              if (typeof props.onUpdated === 'function') {
                await props.onUpdated();
              }
              uiStore.closeModal(confirmId);
              if (props.modalId) {
                uiStore.closeModal(props.modalId);
              }
              toastStore.addToast({
                type: 'success',
                message: `${providerLabel.value}-koppeling verbroken.`,
              });
            } catch (error) {
              uiStore.closeModal(confirmId);
              const message =
                error?.response?.data?.error ||
                error.message ||
                `Kon ${providerLabel.value}-koppeling niet verbreken.`;
              toastStore.addToast({ type: 'error', message });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        selectedCalendarId.value = props.integration?.settings?.calendar_id || '';
        await loadCalendars();
        loading.value = false;
      });

      return {
        loading,
        refreshing,
        saving,
        disconnecting,
        copyingAdminConsent,
        providerLabel,
        calendars,
        selectedCalendarId,
        calendarOptions,
        loadCalendars,
        saveCalendar,
        copyAdminConsentLink,
        disconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .outlook-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__intro {
      margin: 0;
      color: var(--slate-600);
      font-size: 0.875rem;
    }

    &__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: flex-end;
    }

    &__consent {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-200);
    }

    &__hint {
      margin: 0;
      color: var(--slate-500);
      font-size: 0.75rem;
      line-height: 1.4;
    }
  }
</style>
